import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Box } from "@mui/material"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import FormHeader from "../components/FormHeader"
import { ReactComponent as WarningIcon } from "src/v2/assets/icons/warning-error-icon.svg"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import MotorService from "../../../../services/motor-service"
import { dispatchSnackbar } from "../../../components/SnackBar/snackbar-bus"
import TabsWrappedLabel from "../../../components/tabs/tabs-component"
import ParametrosPage from "./Parametros/parametros-page"
import EventosOrdinariosPage from "./EventosOrdinarios/eventos-ordinarios-page"
import EventosExtraordinariosPage from "./EventosExtraordinarios/eventos-extraordinarios-page"
import ValidationPage from "./Validacao/validation-page"
import useValidationData from "./Validacao/hooks/use-validation-data"
import { MODULOS_CADASTRO_EVENTOS } from "./cadastro-motor-eventos-const"
import MotorJurosService from "../../../../services/motor-juros-service"
import { TokenService } from "src/services/token-service"
import { MOTOR_EVENTOS_PAGE } from "src/utils/routes"
import { VALIDATION_MEMORY_COLUMNS } from "./Validacao/validation-memory"

import {createValidationExportMock} from "./Validacao/validation-export.mock" // MOCK TEMPORARIO


const HEADER_DEFAULT = {
    title: `CETIP - | VER -`,
    statusIcon: <WarningIcon />,
    statusColor: "Em Andamento",
    statusLabel: "white",
}

export const MotorCadastroEventosPage = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const isInitialMount = useRef(true)
    const jurosService = useMemo(() => new MotorJurosService(), [])
    const tokenService = useMemo(() => new TokenService(), [])
    const isAdminUser = tokenService.verifyUserIsAdmin()
    const state = location.state
    const cadastroId = state?.RendaFixaCaracteristicasID
    const hasCadastroContext = cadastroId !== null && cadastroId !== undefined
    const pageTitle = "Cadastro de Eventos"
    const [header, setHeader] = useState(HEADER_DEFAULT)
    const [cetip, setCetip] = useState(null)
    const [versaoDocumento, setVersaoDocumento] = useState(null)
    const [caracteristica, setCaracteristica] = useState(null)
    const [idCaracteristica, setIdCaracteristica] = useState(null)
    const [loading, setLoading] = useState(false)
    const motorService = useMemo(() => new MotorService(), [])
    const initialTab = state?.initialTab || MODULOS_CADASTRO_EVENTOS.PARAMETROS
    const [tabValue, setTabValue] = useState(initialTab)
    const [jurosOptions, setJurosOptions] = useState([])
    const [parametrosProducao, setParametrosProducao] = useState(false)
    const [eventosProducao, setEventosProducao] = useState(false)
    const [eventosExtraordinariosProducao, setEventosExtraordinariosProducao] = useState(false)
    const [eventosProjetados, setEventosProjetados] = useState(null)
    const [recompraProgramadaProjetados, setRecompraProgramadaProjetados] = useState(null)
    const [datasAniversarioProjetadas, setDatasAniversarioProjetadas] = useState(null)

    const loadJuros = useCallback(() => {
        jurosService.getCadastroJuros()
            .then((response) => {
                const { body: { results } } = response

                const listJuros = results.map(item => ({
                    IDJuros: item.IDJuros,
                    Calculo: item.Calculo,
                    CalculoDias: item.CalculoDias,
                }))

                setJurosOptions(listJuros || [])
            })
            .catch(() => {
                console.error("Erro ao carregar opções de juros")
            })
    }, [jurosService])

    const setCadastroProducao = useCallback((producao) => {
        if (producao) {
            setHeader((currentHeader) => ({
                ...currentHeader,
                statusLabel: "EM PRODUÇÃO",
                statusIcon: <CheckIcon />
            }))
        }
    }, [])

    const projetarEventos = (eventos, datasAniversario, recompraProgramada) => {
        setEventosProjetados(eventos)
        setDatasAniversarioProjetadas(datasAniversario)
        setRecompraProgramadaProjetados(recompraProgramada)
        setTabValue(MODULOS_CADASTRO_EVENTOS.EVENTOS_ORDINARIOS)
    }

    const parametrosComponent = <ParametrosPage
        producao={parametrosProducao}
        setProducao={setParametrosProducao}
        idCaracteristica={idCaracteristica}
        loading={loading}
        eventosState={state}
        jurosOptions={jurosOptions}
        setLoading={setLoading}
        projetarEventos={projetarEventos}
        caracteristica={caracteristica}
    />

    const eventosOrdinariosComponent = <EventosOrdinariosPage
        idCaracteristica={idCaracteristica}
        producao={eventosProducao}
        setProducao={(value) => {
            setEventosProducao(value)
            setCadastroProducao(value)
        }}
        loading={loading}
        setLoading={setLoading}
        cetip={cetip}
        jurosOptions={jurosOptions}
        versaoDocumento={versaoDocumento}
        caracteristica={caracteristica}
        eventosProjetados={eventosProjetados}
        datasAniversarioProjetadas={datasAniversarioProjetadas}
        recompraProgramadaProjetados={recompraProgramadaProjetados}
    />

    const eventosExtraordinariosComponent = <EventosExtraordinariosPage
        idCaracteristica={idCaracteristica}
        loading={loading}
        setLoading={setLoading}
        cetip={cetip}
        eventosProducao={eventosExtraordinariosProducao}
        setEventosProducao={setEventosExtraordinariosProducao}
        versaoDocumento={versaoDocumento}
        caracteristica={caracteristica}
    />

    const defineHeader = useCallback((Status, CETIP, VersaoDocumento) => {
        const isCadastroEmProducao = Status === "Em Produção"
        const status = isCadastroEmProducao ? "Em Produção" : "Em Andamento"

        setParametrosProducao(isCadastroEmProducao)
        setHeader({
            title: `${CETIP} | VER ${VersaoDocumento}`,
            statusLabel: status.toUpperCase(),
            statusColor: "warning",
            statusIcon: isCadastroEmProducao ? <CheckIcon /> : <WarningIcon />,
        })
    }, [])

    const loadCaracteristicas = useCallback((RendaFixaCaracteristicasID, isRequestCurrent) => {
        motorService.getCadastro(RendaFixaCaracteristicasID)
            .then((response) => {
                if (!isRequestCurrent()) return

                const { body } = response
                setCaracteristica(body)
            })
            .catch(() => {
                if (!isRequestCurrent()) return

                dispatchSnackbar("Não foi possível carregar os dados de cadastro", "error")
            })
    }, [motorService])


    const {
        parameters: validationParameters,
        setParameters: setValidationParameters,
        savedViewModel,
        isLoadingValidation,
        isSavingEmission,
        saveEmission,
        isSavingValidation,
        saveValidation,
        exportExcel,
    } = useValidationData({
        cadastroId,
        motorService,
        onEmissionSaved: loadCaracteristicas,
    })

    const handleExportExcel = useCallback(async (payload) => {
        try {
            // MOCK TEMPORÁRIO: somente as memórias PUPAR e PUMTM.
            // Resultados e prêmios permanecem reais.
            const mockMemories = createValidationExportMock().memories

            const exportPayload = {
                ...payload,
                memories: {
                    ...payload.memories,
                    pupar: mockMemories.pupar,
                    pumtm: mockMemories.pumtm,
                },
            }

            // Após integrar o endpoint, remover o bloco acima
            // e passar payload no lugar de exportPayload.
            const {exported, hasEmptySheets} = await exportExcel(
                exportPayload,
                VALIDATION_MEMORY_COLUMNS
            )

            if (!exported) {
                dispatchSnackbar(
                    "Não há dados para exportar.",
                    "warning"
                )
                return
            }

            dispatchSnackbar(
                hasEmptySheets
                    ? "Download do arquivo iniciado. Algumas abas não possuem dados para exportar."
                    : "Download do arquivo iniciado.",
                "success"
            )
        } catch (error) {
            console.error("Erro ao exportar Excel:", error)
            dispatchSnackbar("Erro ao exportar Excel.", "error")
        }
    }, [exportExcel])


    const tabsData = [
        {
            value: MODULOS_CADASTRO_EVENTOS.PARAMETROS,
            label: 'PARÂMETROS',
            content: parametrosComponent
        },
        {
            value: MODULOS_CADASTRO_EVENTOS.EVENTOS_ORDINARIOS,
            label: 'EVENTOS ORDINÁRIOS',
            content: eventosOrdinariosComponent
        },
        {
            value: MODULOS_CADASTRO_EVENTOS.EVENTOS_EXTRAORDINARIOS,
            label: 'EVENTOS EXTRAORDINÁRIOS',
            content: eventosExtraordinariosComponent
        },
        {
            value: MODULOS_CADASTRO_EVENTOS.VALIDACAO_TAB,
            label: 'VALIDAÇÃO',
            content: (
                <ValidationPage
                    onBack={() => navigate(-1)}
                    isAdminUser={isAdminUser}
                    parameters={validationParameters}
                    onParametersChange={setValidationParameters}
                    savedViewModel={savedViewModel}
                    isLoadingValidation={isLoadingValidation}
                    isSavingEmission={isSavingEmission}
                    onSaveEmission={saveEmission}
                    validationContext={{
                        cetip,
                        versaoDocumento,
                        idCaracteristica,
                        caracteristica,
                    }}
                    onSaveValidation={saveValidation}
                    isSavingValidation={isSavingValidation}
                    onExport={handleExportExcel}
                />
            )
        },
    ]

    useEffect(() => {
        if (!hasCadastroContext) {
            if (isInitialMount.current) {
                dispatchSnackbar("Selecione um cadastro para visualizar os eventos.", "warning")
                navigate(MOTOR_EVENTOS_PAGE, { replace: true })
            }
            return undefined
        }

        isInitialMount.current = false
        let isCurrent = true

        const { RendaFixaCaracteristicasID, Status, CETIP, VersaoDocumento } = state
        defineHeader(Status, CETIP, VersaoDocumento)
        setIdCaracteristica(RendaFixaCaracteristicasID)
        setCetip(CETIP)
        setVersaoDocumento(VersaoDocumento)
        loadCaracteristicas(RendaFixaCaracteristicasID, () => isCurrent)

        return () => {
            isCurrent = false
        }
    }, [defineHeader, hasCadastroContext, loadCaracteristicas, navigate, state])

    useEffect(() => {
        if (!hasCadastroContext) return

        setTabValue(initialTab)
        setEventosProducao(false)
        setEventosExtraordinariosProducao(false)
        setEventosProjetados(null)
        setRecompraProgramadaProjetados(null)
        setDatasAniversarioProjetadas(null)
    }, [cadastroId, hasCadastroContext, initialTab])

    useEffect(() => {
        if (!hasCadastroContext) return

        loadJuros()
    }, [hasCadastroContext, loadJuros])

    if (!hasCadastroContext) return null

    return (
        <SideBarLayout
            title={pageTitle}
            loading={loading}
            header={true}
            headerTitle=""
            headerImage={"/background/eventos_headers.jpg"}
            headerPosition={"1% 41%"}
        >
            <Box
                sx={{
                    marginTop: -14.5,
                    paddingBottom: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    px: { xxxs: 3, md: 3, lg: 0 },
                }}
            >
                <Box mb={4}>
                    <FormHeader
                        title={header.title}
                        statusLabel={header.statusLabel}
                        statusColor={header.statusColor}
                        statusIcon={header.statusIcon}
                    />
                </Box>
            </Box>
            <Box sx={{ mt: -9, position: 'relative', zIndex: 2 }}>
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}>
                    <TabsWrappedLabel
                        key={cadastroId}
                        value={tabValue}
                        onChange={(_, value) => setTabValue(value)}
                        tabs={tabsData} noReload={true} />
                </Box>
            </Box>
        </SideBarLayout>
    )
}

export default MotorCadastroEventosPage
